import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getImageUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  useTheme,
  alpha,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Alert,
  Rating,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  Star as StarIcon,
  Share as ShareIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  CheckCircle as CheckCircleIcon,
  Store as StoreIcon,
  AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import { getVendorById, getVendorAttendedEvents } from '../services/vendorService';

const PublicVendorProfile = () => {
  const theme = useTheme();
  const history = useHistory();
  const { vendorId } = useParams();
  const [activeTab, setActiveTab] = useState(0);

  // Fetch vendor profile
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery(
    ['publicVendorProfile', vendorId],
    () => getVendorById(vendorId),
    {
      retry: 1,
      onError: (error) => {
        console.error('PublicVendorProfile - Query error:', error);
      },
      onSuccess: (data) => {
        console.log('PublicVendorProfile - Query success:', data);
      }
    }
  );

  // Fetch vendor's attended events
  const { data: attendedEvents, isLoading: eventsLoading } = useQuery(
    ['vendorAttendedEvents', vendorId],
    () => getVendorAttendedEvents(vendorId),
    {
      enabled: !!profile,
    }
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: profile?.business_name,
        text: profile?.bio,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard!');
    }
  };

  const openEmail = () => {
    if (profile?.email) {
      window.location.href = `mailto:${profile.email}`;
    }
  };

  if (profileLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading vendor profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (profileError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {profileError.response?.status === 404
            ? 'Vendor profile not found'
            : 'Error loading profile'}
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          No profile found.
        </Alert>
      </Container>
    );
  }

  const stats = {
    totalApplications: profile?.total_applications || 0,
    attendedEvents: attendedEvents?.length || 0,
    averageRating: profile?.average_rating || 0,
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      pb: 4,
      background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.grey[50], 0.8)} 30%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
    }}>
      <Container maxWidth="lg" sx={{ pt: 0 }}>
        {/* Cover Photo Section - LinkedIn/Facebook Style */}
        <Paper 
          elevation={0}
          sx={{
            position: 'relative',
            mb: 3,
            overflow: 'hidden',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 4,
          }}
        >
          <Box
            sx={{ 
              height: { xs: 200, sm: 250, md: 300 },
              position: 'relative',
              background: profile?.cover_photo_url 
                ? 'none'
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          {profile?.cover_photo_url && (
            <img
              src={getImageUrl(profile.cover_photo_url)}
              alt="Cover"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
            />
          )}
        </Box>
        
          {/* Profile Picture and Info Section - Connected */}
          <Box 
            sx={{ 
              position: 'relative', 
              px: { xs: 2, sm: 4, md: 6 }, 
              pb: 4, 
              pt: { xs: 2, sm: 3 },
              background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.grey[50], 0.6)} 50%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderRadius: '0 0 12px 12px',
            }}
          >
            {/* Profile Picture - Large, LinkedIn Style */}
            <Box 
              sx={{ 
                position: 'relative', 
                display: 'inline-block', 
                mb: 3,
                mt: { xs: -8, sm: -10, md: -12 }, // Negative margin to overlap with cover photo
              }}
            >
              <Avatar
                src={profile?.profile_picture_url ? getImageUrl(profile.profile_picture_url) : undefined}
                sx={{
                  width: { xs: 120, sm: 150, md: 180 },
                  height: { xs: 120, sm: 150, md: 180 },
                  border: 5,
                  borderColor: 'background.paper',
                  bgcolor: theme.palette.primary.main,
                  boxShadow: theme.shadows[12],
            }}
          >
            {!profile?.profile_picture_url && (
                  <BusinessIcon sx={{ fontSize: { xs: 60, sm: 75, md: 90 } }} />
            )}
          </Avatar>
              {/* Decorative accent behind avatar */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 140, sm: 170, md: 200 },
                  height: { xs: 140, sm: 170, md: 200 },
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  zIndex: -1,
                }}
              />
            </Box>

            {/* Main Info Section */}
            <Grid container spacing={3} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.75),
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    backdropFilter: 'blur(12px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  {profile?.business_name || 'Business Name'}
                </Typography>
                    {profile?.verified && (
                      <Tooltip title="Verified Business">
                        <VerifiedIcon color="primary" sx={{ fontSize: 28 }} />
                      </Tooltip>
                    )}
                  </Box>

                  {profile?.category_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CategoryIcon color="primary" fontSize="small" />
                      <Chip
                        label={profile.category_name}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}

                  {/* Bio/Description */}
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.7, fontSize: '1.05rem' }}
                  >
                    {profile?.bio || 'No description available.'}
                  </Typography>

                  {/* Contact Info Chips */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {profile?.contact_info && (
                <Chip
                  icon={<PhoneIcon />}
                  label={profile.contact_info}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                />
              )}
              {profile?.email && (
                <Chip
                  icon={<EmailIcon />}
                  label={profile.email}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                      />
                    )}
                    {profile?.location && (
                      <Chip
                        icon={<LocationIcon />}
                        label={profile.location}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.warning.main, 0.12),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        }}
                      />
                    )}
                  </Box>

                  {/* Social Media Links - Prominent Display with Text */}
                  {profile?.social_media && Object.keys(profile.social_media).some(key => profile.social_media[key]) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                        Connect With Us
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {profile.social_media.facebook && profile.social_media.facebook.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<FacebookIcon />}
                            onClick={() => {
                              const url = profile.social_media.facebook;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#1877F2', 0.05),
                              borderColor: alpha('#1877F2', 0.3),
                              color: '#1877F2',
                              '&:hover': { 
                                bgcolor: alpha('#1877F2', 0.1),
                                borderColor: '#1877F2',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Facebook
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.facebook.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.twitter && profile.social_media.twitter.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<TwitterIcon />}
                            onClick={() => {
                              const url = profile.social_media.twitter;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#1DA1F2', 0.05),
                              borderColor: alpha('#1DA1F2', 0.3),
                              color: '#1DA1F2',
                              '&:hover': { 
                                bgcolor: alpha('#1DA1F2', 0.1),
                                borderColor: '#1DA1F2',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Twitter
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.twitter.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.instagram && profile.social_media.instagram.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<InstagramIcon sx={{ color: '#E4405F' }} />}
                            onClick={() => {
                              const url = profile.social_media.instagram;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#E4405F', 0.05),
                              borderColor: alpha('#E4405F', 0.3),
                              color: '#E4405F',
                              '&:hover': { 
                                bgcolor: alpha('#E4405F', 0.1),
                                borderColor: '#E4405F',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Instagram
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.instagram.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.linkedin && profile.social_media.linkedin.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<LinkedInIcon sx={{ color: '#0077B5' }} />}
                            onClick={() => {
                              const url = profile.social_media.linkedin;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#0077B5', 0.05),
                              borderColor: alpha('#0077B5', 0.3),
                              color: '#0077B5',
                              '&:hover': { 
                                bgcolor: alpha('#0077B5', 0.1),
                                borderColor: '#0077B5',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                LinkedIn
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.linkedin.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.website && profile.social_media.website.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<WebsiteIcon />}
                            onClick={() => {
                              const url = profile.social_media.website;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha(theme.palette.grey[500], 0.05),
                              borderColor: alpha(theme.palette.grey[500], 0.3),
                              color: 'text.primary',
                              '&:hover': { 
                                bgcolor: alpha(theme.palette.grey[500], 0.1),
                                borderColor: theme.palette.grey[500],
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Website
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.website.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Button 
                      variant="contained" 
                      startIcon={<EmailIcon />} 
                      onClick={openEmail}
                      disabled={!profile?.email}
                      sx={{ borderRadius: 2 }}
                    >
                      Contact
                    </Button>
                    <Button 
                      variant="outlined" 
                      startIcon={<ShareIcon />} 
                      onClick={handleShare}
                      sx={{ borderRadius: 2 }}
                    >
                      Share Profile
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* Stats Cards Sidebar */}
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  {/* Rating Card */}
                  <Card sx={{ 
                    borderRadius: 3, 
                    boxShadow: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                  }}>
                <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Rating
                        </Typography>
                        <StarIcon sx={{ color: theme.palette.warning.main }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : 'N/A'}
                  </Typography>
                        {stats.averageRating > 0 && (
                          <Rating value={stats.averageRating} readOnly precision={0.1} size="small" />
                        )}
                      </Box>
                  <Typography variant="body2" color="text.secondary">
                        Average rating from reviews
                  </Typography>
                </CardContent>
              </Card>

                  {/* Events Attended Card */}
                  <Card sx={{ 
                    borderRadius: 3, 
                    boxShadow: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  }}>
                <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Events Attended
                        </Typography>
                        <CheckCircleIcon color="success" />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                        {stats.attendedEvents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                        Successful participations
                  </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
          </Box>
        </Paper>

      {/* Tabs Section */}
        <Paper 
          sx={{ 
            borderRadius: 3, 
            boxShadow: 4, 
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
          <Tab label="About" />
          <Tab label="Gallery" />
          <Tab label="Events" />
          <Tab label="Reviews" />
        </Tabs>
          </Box>

        {/* About Tab */}
        {activeTab === 0 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.4) }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.85),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  About
                    </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: 'text.primary' }}>
                  {profile?.bio || profile?.about || 'No description available.'}
                    </Typography>
              </Paper>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Contact Information
                    </Typography>
              <Grid container spacing={2}>
                      {profile?.email && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.success.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                          }}
                        >
                          <EmailIcon color="success" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                      )}
                      {profile?.contact_info && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                          }}
                        >
                          <PhoneIcon color="info" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.contact_info}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                {profile?.location && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.warning.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.12),
                          }}
                        >
                          <LocationIcon color="warning" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.location}
                          </Typography>
                        </Box>
                    </Box>
                    </Paper>
                  </Grid>
                )}
            </Grid>
          </Box>
        )}

        {/* Gallery Tab */}
        {activeTab === 1 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.4) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Gallery
              </Typography>
              {profile?.gallery && profile.gallery.length > 0 ? (
                <ImageList cols={3} gap={12} sx={{ mt: 0 }}>
                  {profile.gallery.map((image, index) => {
                    const fullUrl = getImageUrl(image.image_url);
                    return (
                  <ImageListItem key={image.id || index}>
                    <img
                          src={fullUrl}
                          alt={image.caption || `Gallery image ${index + 1}`}
                      loading="lazy"
                          style={{ 
                            height: 280, 
                            objectFit: 'cover',
                            borderRadius: 8,
                            cursor: 'pointer',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                          onClick={() => window.open(fullUrl, '_blank')}
                        />
                        {image.caption && (
                    <ImageListItemBar
                            title={image.caption}
                            sx={{
                              borderBottomLeftRadius: 8,
                              borderBottomRightRadius: 8,
                            }}
                          />
                        )}
                  </ImageListItem>
                    );
                  })}
              </ImageList>
            ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AddPhotoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No images in gallery yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This vendor hasn't uploaded any gallery images.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Events Tab */}
        {activeTab === 2 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.4) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Attended Events
              </Typography>
              {eventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : attendedEvents?.length > 0 ? (
                <Grid container spacing={3}>
                {attendedEvents.map((event) => (
                    <Grid item xs={12} sm={6} md={4} key={event.id || event.event_id}>
                      <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardMedia
                        component="img"
                          height="180"
                          image={event.image_url ? getImageUrl(event.image_url) : 'https://via.placeholder.com/400x180'}
                        alt={event.title}
                          sx={{ objectFit: 'cover' }}
                      />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom noWrap>
                          {event.title}
                        </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                          {new Date(event.date).toLocaleDateString()}
                        </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {event.location}
                        </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                              Successfully Participated
                            </Typography>
                          </Box>
                      </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => history.push(`/event/${event.id || event.event_id}`)}>
                            View Event
                          </Button>
                        </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No events attended yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This vendor hasn't attended any events yet.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Reviews Tab */}
        {activeTab === 3 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.4) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Reviews
              </Typography>
              {profile?.reviews && profile.reviews.length > 0 ? (
                <Stack spacing={3}>
                {profile.reviews.map((review, index) => (
                    <Card key={index} sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar src={review.user_avatar}>
                          {review.user_name?.charAt(0) || 'U'}
                        </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {review.user_name || review.user_name || 'Anonymous'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {review.date ? new Date(review.date).toLocaleDateString() : 'No date'}
                          </Typography>
                          </Box>
                          <Rating value={review.rating || 0} readOnly />
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                          {review.comment || review.review || 'No review text provided.'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <StarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No reviews yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This vendor hasn't received any reviews yet.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Container>
    </Box>
  );
};

export default PublicVendorProfile;
